import * as React from 'react';
import styled from 'styled-components';
import { motion, Transition } from 'framer-motion';

import { NftButtonOptions } from './CardButtonContainer';
import { CardButton } from './CardButton';

interface Props {
  buttonData: NftButtonOptions[];
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  ignoreBlurRef: React.MutableRefObject<boolean>;
}

/**
 * Dropdown menu for a NotificationCard that contains a list of buttons.
 */
export const CardDropdownMenu: React.FC<Props> = (props) => {
  const { buttonData, onBlur, ignoreBlurRef } = props;

  const focusAndPositionRef = (node: HTMLDivElement) => {
    if (node) {
      node.focus();
      const buttonContainer = node.parentElement?.parentElement;
      const dropdownButton = node.parentElement;

      const buttonContainerBounds = buttonContainer?.getBoundingClientRect();
      const dropdownButtonBounds = dropdownButton?.getBoundingClientRect();
      const menuBounds = node.getBoundingClientRect();

      if (!buttonContainerBounds || !dropdownButtonBounds) {
        return;
      }

      const containerLeft = dropdownButtonBounds.x - buttonContainerBounds.x;
      let width = menuBounds.width;
      let x = width - dropdownButtonBounds.width;

      if (width >= buttonContainerBounds.width) {
        width = buttonContainerBounds.width;
        x = containerLeft;
      } else if (x > containerLeft) {
        x = containerLeft;
      }

      node.style.left = `${-x}px`;
      node.style.width = `${width}px`;
    }
  };

  // TODO: [WRK-641] Disable animations due to `backgroundThrottling` bug in runtime 19.89.*
  const variants = {
    /**
     * We need the scale to be 1 so we can position the
     * menu in the correct place
     */
    initial: {
      scale: 1,
      opacity: 0,
    },
    visible: {
      opacity: 1,
      scale: [0, 0.5, 1],
      transition: {
        type: 'spring',
        times: [0, 0, 1],
        duration: 0.15,
        bounce: 0.25,
      },
    },
    hidden: {
      opacity: 0,
      scale: 0,
    },
  };

  const transition: Transition = {
    type: 'spring',
    duration: 0.2,
    bounce: 0.25,
  };

  const handleMouseDown = () => {
    ignoreBlurRef.current = true;
  };

  return (
    <Container
      ref={focusAndPositionRef}
      onBlur={onBlur}
      tabIndex={1}
      variants={variants}
      initial="initial"
      exit="hidden"
      animate="visible"
      transition={transition}
      key="dropdown"
    >
      {buttonData.map((button, i) => (
        <CardButton className="menu-item" key={i} {...button} onMouseDown={handleMouseDown} />
      ))}
    </Container>
  );
};

const Container = styled(motion.div)`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: absolute;
  bottom: ${(props) => props.theme.px.xxxlarge};
  max-width: max-content;
  min-width: 80px;
  background: ${(props) => props.theme.palette.background4};
  box-shadow: ${(props) => props.theme.shadow.base};
  z-index: 100;

  &:focus {
    outline: none;
  }

  .menu-item {
    border-radius: 0;
    background: none;
    border: transparent;
    width: 100%;
    max-width: 288px;
    flex: 1 0 ${(props) => props.theme.px.xxlarge};
    padding: ${(props) => props.theme.px.xsmall} ${(props) => props.theme.px.base};
    & > * {
      text-align: left;
    }

    &:focus,
    &:hover {
      outline: none;
      border-color: transparent;
      background: ${(props) => props.theme.palette.background2};
    }
  }
`;
